import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ServicesList = ({ services, onEdit, onDelete, onToggleStatus, limit }) => {
  const { t } = useTranslation();
  if (services.length === 0) return null;

  const visibleServices = limit ? services.slice(0, limit) : services;

  return (
    <div className="bg-white p-6 rounded-2xl border border-gray-100 mb-8" data-testid="my-services-section">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{t('ux.myServices')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visibleServices.map((service) => {
          const isPaused = service.status === 'paused';
          return (
          <div
            key={service.id}
            className={`border rounded-xl p-4 hover:shadow-sm transition ${isPaused ? 'border-gray-200 bg-gray-50' : 'border-gray-100 hover:border-gray-200'}`}
            data-testid={`service-item-${service.id}`}
          >
            <div className="flex justify-between items-start">
              <Link to={`/services/${service.id}`} className={`flex-1 ${isPaused ? 'opacity-60' : ''}`}>
                <h3 className="font-bold text-gray-900 hover:text-gray-700">{service.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{service.category}</p>
                <p className="text-gray-900 font-semibold mt-2">
                  {service.currency === 'PHP' ? '₱' : '$'}{service.price?.toLocaleString()} / {service.price_unit}
                </p>
              </Link>
              <div className="flex flex-col items-end gap-2">
                {onToggleStatus && (
                  <button
                    onClick={() => onToggleStatus(service)}
                    title={isPaused ? 'Paused — hidden from clients. Tap to activate.' : 'Active — visible to clients. Tap to pause.'}
                    className={`text-xs font-semibold px-3 py-1 rounded-full transition flex items-center gap-1.5 ${isPaused ? 'bg-gray-200 text-gray-600 hover:bg-gray-300' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                    data-testid={`toggle-status-${service.id}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${isPaused ? 'bg-gray-400' : 'bg-green-500'}`} />
                    {isPaused ? t('ux.paused') : t('ux.active')}
                  </button>
                )}
                {service.rating > 0 && (
                  <div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-full">
                    <svg className="w-4 h-4 text-yellow-500 fill-yellow-500" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">{service.rating}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(service)}
                    className="text-gray-500 hover:text-gray-700 p-1.5 hover:bg-gray-100 rounded-lg transition"
                    data-testid={`edit-service-${service.id}`}
                  >
                    ✏️ {t('ux.edit')}
                  </button>
                  <button
                    onClick={() => onDelete(service.id)}
                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                    data-testid={`delete-service-${service.id}`}
                  >
                    🗑️ {t('ux.delete')}
                  </button>
                </div>
                {service.images?.[0] && (
                  <img
                    src={service.images[0]}
                    alt={service.title}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="w-14 h-14 rounded-lg object-cover border border-gray-200 mt-1"
                    data-testid={`service-thumb-${service.id}`}
                  />
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServicesList;
